import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { isAuthenticated } from 'Auth';
import Api from 'Api';
import './../public/assets/css/reactdatepicker.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-tooltip/dist/react-tooltip.css';

/*
 * Todo callback de ResizeObserver passa a rodar no frame seguinte.
 *
 * O navegador entrega as notificações de redimensionamento entre o cálculo do
 * layout e a pintura. Se um callback altera o layout nesse intervalo, sobram
 * notificações não entregues e o navegador dispara
 * "ResizeObserver loop completed with undelivered notifications" na window — o
 * que o overlay do dev server exibe como se fosse uma exceção.
 *
 * Adiar um frame quebra o ciclo na origem, para todos os observers do app
 * (ActionMenu, react-tooltip, editor Lexical, comparador de imagens). O atraso
 * é imperceptível, e nenhum erro real deixa de aparecer — diferente de filtrar
 * a mensagem, que só esconderia o sintoma.
 */
if (typeof window !== 'undefined' && window.ResizeObserver) {
	const NativeResizeObserver = window.ResizeObserver;

	window.ResizeObserver = class extends NativeResizeObserver {
		constructor(callback) {
			super((entries, observer) => {
				window.requestAnimationFrame(() => callback(entries, observer));
			});
		}
	};
}
import './../public/assets/css/layout.css';
import 'custom.css';
import 'follow.css';
import Environments from './environments';
import { ThemeProvider } from '@emotion/react';
import { theme } from './styles/app-theme';
import { ReportExportsProvider } from 'contexts/ReportExportsContext';

const App = lazy(() => import('App'));
const Join = lazy(() => import('Join'));
const Login = lazy(() => import('Login'));
const BotoxToPrint = lazy(() => import('pages/BotoxToPrint'));
const Telehealth = lazy(() => import('pages/TelehealthPatientRoute'));
const PrenatalPublicView = lazy(() => import('pages/PrenatalPublicView'));
const ChamadorView = lazy(() => import('pages/ChamadorView'));
const TotemTicketSelect = lazy(() => import('pages/Totem/TotemTicketSelect'));
const TotemCPFInput = lazy(() => import('pages/Totem/TotemCPFInput'));
const TotemConfirmation = lazy(() => import('pages/Totem/TotemConfirmation'));

const ValidatorPage = lazy(() => import('pages/Validator'));

export const redirectShortUrlToOriginal = async () => {
	const pathMatch = window.location.pathname.match(/^\/s\/([^/]+)$/);
	if (pathMatch) {
		try {
			const shortCode = pathMatch[1];
			const response = await Api.getOriginalUrl(shortCode);
			const { original_url } = response.data;

			if (original_url) {
				window.location.href = original_url;
			} else {
				throw new Error('URL original não encontrada.');
			}
		} catch (error) {
			console.error('Erro ao redirecionar:', error);
			window.location.pathname = 'link-not-exists';
		}
	}
};

Api.getEnvs()
	.then(async response => {
		Environments.setEnvs(response.data);

		// Se for uma short URL válida, para a execução e redireciona
		const redirected = await redirectShortUrlToOriginal();
		if (redirected) return;

		// Configuração de cores baseada na plataforma
		// Aplicar variáveis CSS globais
		document.documentElement.style.setProperty('--primary-color', Environments.platform.customStyle.palette.primary);
		document.documentElement.style.setProperty('--secondary-color', Environments.platform.customStyle.palette.secondary);
		document.documentElement.style.setProperty('--tertiary-color', Environments.platform.customStyle.palette.tertiary);
		document.documentElement.style.setProperty('--quaternary-color', Environments.platform.customStyle.palette.quaternary);

		// Atualizar título e favicon
		document.title = Environments.platform.name;

		// Remover favicons existentes
		const existingFavicons = document.querySelectorAll("link[rel*='icon']");
		existingFavicons.forEach(favicon => favicon.remove());

		// Criar novo favicon
		const favicon = document.createElement('link');
		favicon.rel = 'icon';
		favicon.type = 'image/x-icon';
		favicon.href = Environments.platform.favicon;
		document.head.appendChild(favicon);

		if (Environments.platform?.name?.toLowerCase() === 'medclinic') {
			await import('./medclinic.css');
		}

		const theme = {
			colors: {
				primary: Environments.envs.customStyle.palette.primary,
				secondary: Environments.envs.customStyle.palette.secondary,
				tertiary: Environments.envs.customStyle.palette.tertiary,
				dark: '#1A1D20',
				danger: '#D93B3B',
				dangerHover: '#B02A2A',
				surface: '#2A2E31',
				textSecondary: '#A9B4B4',

				border: 'rgba(240, 235, 225, 0.1)'
			},
			spacing: {
				small: '8px',
				medium: '16px',
				large: '24px'
			},
			borderRadius: '8px'
		};

		const EntryPoint = () => {
			const AuthRouteHandler = () => {
				// Logado e no /login (favorito, autocomplete, a aba que recarregou porque outra entrou):
				// vai para o início. Limpar o storage aqui não encerrava a sessão (ela está no cookie) e
				// derrubava as outras abas, que recarregam quando o storage é limpo — com duas abas no
				// /login, uma desfazia o login da outra em laço.
				if (isAuthenticated() && window.location.pathname === '/login') {
					window.location.replace('/');
					return null;
				}
				// MF-1143: o painel de exportações envolve o app logado para sobreviver à navegação entre telas
				if (isAuthenticated())
					return (
						<ReportExportsProvider>
							<App />
						</ReportExportsProvider>
					);
				return <Login />;
			};

			const DefaultComponent = () => {
				return (
					<BrowserRouter>
						<Switch>
							<Route path='/join' component={Join} />
							<Route path='/botox-to-print' component={BotoxToPrint} />
							<Route exact path='/telehealth' component={Telehealth} />
							<Route path='/prenatal-share/:id' component={PrenatalPublicView} />
							<Route path='/chamador/:callerCode' component={ChamadorView} />
							<Route exact path='/totem/:receptionCode/:ticketId/confirmacao' component={TotemConfirmation} />
							<Route exact path='/totem/:receptionCode/:ticketId' component={TotemCPFInput} />
							<Route exact path='/totem/:receptionCode' component={TotemTicketSelect} />
							<Route exact path='/validation/:code' component={ValidatorPage} />
							<Route component={AuthRouteHandler} />
						</Switch>
					</BrowserRouter>
				);
			};

			return (
				<React.StrictMode>
					<Suspense fallback={<div></div>}>
						<ThemeProvider theme={theme}>
							<DefaultComponent />
						</ThemeProvider>
					</Suspense>
				</React.StrictMode>
			);
		};

		ReactDOM.render(<EntryPoint />, document.getElementById('root'));
	})
	.catch(error => {
		console.error('Error fetching environment variables:', error);
		setTimeout(() => {
			window.location.reload();
		}, 1000);
	});

if (window.location.host === 'localhost:3100' && module.hot) {
	module.hot.accept();
}
